// DashboardNavbar.js
import React, { useState, useEffect, useContext } from "react";
import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem"; // <-- Import MenuItem
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  setDarkMode,
} from "context";
import { signOut } from "aws-amplify/auth";
import MDTypography from "components/MDTypography";
import { PopupContext } from "context/popupContext";

function DashboardNavbar({ absolute, light, isMini, name }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(null);
  const route = useLocation().pathname.split("/").slice(1);

  // Pull in your popup queue + new openPopupAtIndex function
  const { popupQueue, openPopupAtIndex } = useContext(PopupContext);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(null);

  // When a user clicks a notification in the menu
  const handleNotificationClick = (index) => {
    openPopupAtIndex(index);
    handleCloseMenu();
  };
  const reversedQueue = popupQueue.slice().reverse();

  // Define a function to render the notifications menu
  const renderMenu = (
      <Menu
          anchorEl={openMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(openMenu)}
          onClose={handleCloseMenu}
          sx={{ mt: 2 }}
      >
        {reversedQueue && reversedQueue.length > 0 ? (
            reversedQueue.map((message, i) => {
              // i is the index in the reversed array, so
              // the actual index in the original array is:
              const actualIndex = reversedQueue.length - 1 - i;

              return (
                  <MenuItem key={actualIndex} onClick={() => handleNotificationClick(actualIndex)}>
                    <NotificationItem icon={<Icon>notifications</Icon>} title={message.message} />
                  </MenuItem>
              );
            })
        ) : (
            <Box sx={{ p: 2 }}>No notifications</Box>
        )}
      </Menu>
  );

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  const handleDarkMode = () => setDarkMode(dispatch, !darkMode);

  return (
      <AppBar
          position={absolute ? "absolute" : navbarType}
          color="inherit"
          sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
      >
        <Toolbar sx={(theme) => navbarContainer(theme)}>
          <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
            <Breadcrumbs icon="home" title={name ? name : route[route.length - 1]} route={route} light={light} />
          </MDBox>
          {!isMini && (
            <MDBox sx={{ flex: 1, display: "flex", justifyContent: "center", px: 2 }}>
              <MDTypography
                sx={{
                  color: "#d32f2f",
                  fontWeight: 900,
                  fontSize: { xs: "1rem", md: "1.75rem" },
                  lineHeight: 1.1,
                  textAlign: "center",
                }}
              >
                !מיועד לבדיקות, לא לעבוד על ממשק זה
              </MDTypography>
            </MDBox>
          )}
          {isMini ? null : (
              <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
                <MDBox color={light ? "white" : "inherit"}>
                  <Link to="/authentication/sign-in/basic">
                    <IconButton sx={navbarIconButton} size="small" disableRipple>
                      <Icon sx={iconsStyle}>account_circle</Icon>
                    </IconButton>
                  </Link>
                  <IconButton size="small" disableRipple sx={navbarMobileMenu} onClick={handleMiniSidenav}>
                    <Icon sx={iconsStyle} fontSize="medium">
                      {miniSidenav ? "menu_open" : "menu"}
                    </Icon>
                  </IconButton>
                  <IconButton size="small" disableRipple sx={navbarIconButton} onClick={handleConfiguratorOpen}>
                    <Icon sx={iconsStyle}>settings</Icon>
                  </IconButton>
                  <IconButton
                      size="small"
                      disableRipple
                      color="inherit"
                      sx={navbarIconButton}
                      aria-controls="notification-menu"
                      aria-haspopup="true"
                      variant="contained"
                      onClick={handleOpenMenu}
                  >
                    <Badge badgeContent={popupQueue?.length} color="error">
                      <Icon sx={iconsStyle}>notifications</Icon>
                    </Badge>
                  </IconButton>
                  <Button
                      variant="contained"
                      sx={{
                        color: "#fff",
                        backgroundColor: "red",
                        marginLeft: "10px",
                        ":hover": { backgroundColor: "red", color: "#fff" },
                      }}
                      onClick={async () => await signOut()}
                  >
                    Sign Out
                  </Button>
                  {renderMenu}
                </MDBox>
              </MDBox>
          )}
        </Toolbar>
      </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
